import React from 'react';
import { User } from 'lucide-react';
import { Link } from 'react-router-dom';

interface AvatarData {
  id?: string;
  user_id?: string;
  avatar_url?: string;
  username?: string;
}

interface AvatarGroupProps {
  avatars: AvatarData[];
  max?: number;
  size?: 'xs' | 'sm' | 'md';
  showCount?: boolean;
  totalCount?: number;
  className?: string;
}

const sizeClasses = {
  xs: 'h-6 w-6 text-[8px]',
  sm: 'h-8 w-8 text-[10px]',
  md: 'h-10 w-10 text-xs',
};

const overlapClasses = {
  xs: '-ml-2',
  sm: '-ml-2.5',
  md: '-ml-3',
};

export function AvatarGroup({
  avatars,
  max = 4,
  size = 'sm',
  showCount = true,
  totalCount,
  className = '',
}: AvatarGroupProps) {
  const displayAvatars = avatars.slice(0, max);
  const remainingCount = (totalCount ?? avatars.length) - displayAvatars.length;

  return (
    <div className={`flex items-center ${className}`}>
      {displayAvatars.map((avatar, index) => (
        <div
          key={avatar.user_id || avatar.id || index}
          className={`${sizeClasses[size]} rounded-full border-2 border-white bg-gray-100 overflow-hidden flex-shrink-0 ${
            index > 0 ? overlapClasses[size] : ''
          }`}
          style={{ zIndex: displayAvatars.length - index }}
        >
          {avatar.avatar_url ? (
            <img
              src={avatar.avatar_url}
              alt={avatar.username || 'User'}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-gray-100">
              <User className="h-1/2 w-1/2 text-gray-400" />
            </div>
          )}
        </div>
      ))}
      {showCount && remainingCount > 0 && (
        <AvatarGroupCount count={remainingCount} size={size} />
      )}
    </div>
  );
}

interface AvatarGroupCountProps {
  count: number;
  size?: 'xs' | 'sm' | 'md';
}

export function AvatarGroupCount({ count, size = 'sm' }: AvatarGroupCountProps) {
  return (
    <div
      className={`${sizeClasses[size]} ${overlapClasses[size]} rounded-full border-2 border-white bg-accent-text/10 flex items-center justify-center flex-shrink-0`}
      style={{ zIndex: 0 }}
    >
      <span className="font-medium text-accent-text">+{count > 99 ? '99' : count}</span>
    </div>
  );
}
